import { useEffect } from "react";
import { APPOINTEDD_DEFAULT_FAVICON_URL } from "../../../../shared/node/BrandConfig";

/**
 * Base page title, matching the static `<title>` previously hardcoded in
 * `index.htm` (now dynamic, see `BrandHeadTags` below).
 */
export const BASE_PAGE_TITLE = "Appointedd - Booking Tools";
export type BrandHeadTagsProps = {
  /**
   * A suffix appended to the base page/tab title (e.g.
   * `"Appointedd - Booking Tools | <pageTitleSuffix>"`) when present.
   * Falls back to just the base title when unset.
   */
  pageTitleSuffix?: string | null;
  /**
   * The favicon URL to display in the browser tab. Falls back to
   * Appointedd's default favicon (`APPOINTEDD_DEFAULT_FAVICON_URL`) when
   * unset.
   */
  faviconUrl?: string | null;
};

/**
 * `id` used for the injected `<link rel="icon">` tag, so repeated
 * invocations (e.g. across re-renders, or multiple widget instances on the
 * same page) update the same tag rather than appending duplicates.
 */
export const FAVICON_LINK_ID = "ap-booking-tools-favicon";

/**
 * Sets `document.title` (base title, plus `" | " + pageTitleSuffix` when
 * provided) and injects/updates a `<link rel="icon">` tag in
 * `document.head` pointing at `faviconUrl` (falling back to Appointedd's
 * default favicon when unset).
 *
 * There is no pre-existing dynamic title/favicon mechanism in this app —
 * `index.htm`'s `<title>` and lack of a `<link rel="icon">` were both fully
 * static prior to this component. Unlike `BrandStyleTag`, title/favicon
 * have no per-theme (light/dark) variant and don't need to react live to
 * OS/visitor theme changes — they're just set once brand data resolves,
 * and re-set if `pageTitleSuffix`/`faviconUrl` themselves change (e.g. a
 * re-render with different brand data).
 *
 * Renders nothing — this is a pure side-effect component, mirroring
 * `BrandStyleTag`'s `useEffect`-based approach for consistency.
 */
export function BrandHeadTags({
  pageTitleSuffix,
  faviconUrl
}: BrandHeadTagsProps): null {
  useEffect(() => {
    document.title = pageTitleSuffix ? `${BASE_PAGE_TITLE} | ${pageTitleSuffix}` : BASE_PAGE_TITLE;
  }, [pageTitleSuffix]);
  useEffect(() => {
    const href = faviconUrl ?? APPOINTEDD_DEFAULT_FAVICON_URL;
    let link = document.getElementById(FAVICON_LINK_ID) as HTMLLinkElement | null;
    if (!link) {
      // Guard against a pre-existing, non-Appointedd-managed favicon link
      // (there shouldn't be one — index.htm has no `<link rel="icon">` —
      // but check defensively rather than assume and risk a duplicate).
      link = document.querySelector("link[rel~='icon']") ?? document.createElement("link");
      link.id = FAVICON_LINK_ID;
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = href;
  }, [faviconUrl]);
  return null;
}
export default BrandHeadTags;